'use client';

import { useState, useRef, useEffect } from 'react';
import { useUser, useOrganization, useOrganizationList } from '@clerk/nextjs';
import useHardSignOut from '@/hooks/useHardSignOut';
import Link from 'next/link';
import {
  UserCircleIcon,
  ChevronDownIcon,
  CreditCardIcon,
  ArrowLeftOnRectangleIcon,
  Cog6ToothIcon,
  PlusIcon,
  QuestionMarkCircleIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/outline';
import { useTranslations } from 'next-intl';
import ThemeSwitcher from './ThemeSwitcher';
import LanguageSwitcher from './LanguageSwitcher';
import OrgSwitcher from './OrgSwitcher';
// Admin website-first creation modal (scrape + background onboarding) — the
// same one used on /admin/organizations. Operator-gated backend, so the
// trigger below is gated on isOperator (not isAdmin, which includes agents).
import CreateOrganizationModal from '@/app/admin/organizations/CreateOrganizationModal';
import useUserPermissions from '@/hooks/useUserPermissions';
import { PartnerPortalLink } from './partner/PartnerPortalLink';
import { usePortalHref } from '@/hooks/usePortal';
import { useConvexAuth, useQuery, useAction } from 'convex/react';
import { api } from '../../convex/_generated/api';
import { useHelp } from './HelpProvider';

interface UserMenuProps {
  collapsed: boolean;
  dropdownDirection?: 'up' | 'down';
  /** Horizontal anchor for the dropdown. 'right' is used when the trigger sits
   *  at the right edge of a top bar (mobile shell) so the menu opens inward. */
  dropdownAlign?: 'left' | 'right';
  /** Hide entries that lead to desktop-only destinations. Used by the mobile
   *  shell, where /admin and the partner portal show the "not available on
   *  mobile" notice. Support stays visible — the support modal works anywhere. */
  hideDesktopOnlyLinks?: boolean;
  /** Render an org-switcher section inside the dropdown. Enabled by the mobile
   *  shell (MobileTopNav), whose top bar is the only chrome and otherwise has
   *  no way to switch orgs — the desktop sidebar already has its own OrgSwitcher
   *  up top, so it leaves this off to avoid duplicating it. */
  showOrgSwitcher?: boolean;
}

export default function UserMenu({ collapsed, dropdownDirection = 'down', dropdownAlign = 'left', hideDesktopOnlyLinks = false, showOrgSwitcher = false }: UserMenuProps) {
  const t = useTranslations('userMenu');
  // Gate on Convex auth, not Clerk's isSignedIn (sign-out race, see src/lib/authError.ts)
  const { isAuthenticated } = useConvexAuth();
  const { user, isLoaded } = useUser();
  const { organization: activeOrg } = useOrganization();
  const { userMemberships } = useOrganizationList({
      userMemberships: {
        infinite: true,
        pageSize: 10,
      },
  });
  const [isOpen, setIsOpen] = useState(false);
  const [showCreateOrgModal, setShowCreateOrgModal] = useState(false);
  const handleSignOut = useHardSignOut();
  const { isAdmin, isOperator } = useUserPermissions();
  // Opens the standalone admin console (admin.42a.ai, or admin.localtest.me in
  // dev) in a new tab. Resolves the subdomain from the current host.
  const adminHref = usePortalHref('admin', '/admin');
  const { openSupport } = useHelp();
  const menuRef = useRef<HTMLDivElement>(null);
  const organizations = useQuery(api.organizations.getOrganizations, isAuthenticated ? {} : 'skip');
  const org = organizations?.[0];
  const hasPaymentOverride = org?.paymentOverride != null;
  // Settings is available to org admins of the active org, and to 42a admins —
  // including operators impersonating an org they aren't a Clerk member of.
  const isOrgAdmin =
    !!activeOrg &&
    userMemberships.data?.find((m) => m.organization.id === activeOrg.id)?.role === 'org:admin';
  const canAccessSettings = isOrgAdmin || isAdmin === true;
  const getBillingPortalUrl = useAction(api.features.billing.lsActions.getBillingPortalUrl);
  const [isOpeningBilling, setIsOpeningBilling] = useState(false);

  const handleOpenBillingPortal = async () => {
    if (isOpeningBilling) return;
    setIsOpeningBilling(true);
    setIsOpen(false);
    const newTab = window.open('', '_blank');
    try {
      const { portalUrl } = await getBillingPortalUrl({});
      if (newTab) {
        newTab.location.href = portalUrl;
      } else {
        window.open(portalUrl, '_blank', 'noopener,noreferrer');
      }
    } catch (err) {
      newTab?.close();
      console.error('Failed to open billing portal', err);
    } finally {
      setIsOpeningBilling(false);
    }
  };

  // Close menu when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  if (!isLoaded || !user) return null;

  const opensDown = dropdownDirection === 'down';
  const dropdownPositionClass = dropdownAlign === 'right'
    ? (opensDown ? 'right-0 top-full mt-2' : 'right-0 bottom-full mb-2')
    : opensDown
    ? (collapsed ? 'left-full ml-2 top-0' : 'left-0 top-full mt-2 w-full')
    : (collapsed ? 'left-full ml-2 bottom-0' : 'left-0 bottom-full mb-2 w-full');

  return (
    <div className="relative" ref={menuRef}>
      {/* Trigger Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center w-full p-2 rounded-lg transition-all hover:bg-[var(--color-surface-hover)] ${
          collapsed ? 'justify-center' : 'justify-between'
        }`}
      >
        <div className="flex items-center gap-3 min-w-0">
          {user.imageUrl ? (
            <img
              src={user.imageUrl}
              alt={user.fullName || user.emailAddresses[0]?.emailAddress || 'User'}
              className="w-8 h-8 rounded-full ring-2 ring-lime-400/30 flex-shrink-0"
            />
          ) : (
            <UserCircleIcon className="w-8 h-8 flex-shrink-0" style={{ color: 'var(--color-text-muted)' }} />
          )}

          {!collapsed && (
            <div className="text-left min-w-0 flex-1">
               <p className="text-sm font-medium truncate" style={{ color: 'var(--color-text-primary)' }}>
                {user.fullName || user.emailAddresses[0]?.emailAddress}
              </p>
               <p className="text-xs truncate" style={{ color: 'var(--color-text-muted)' }}>
                {user.emailAddresses[0]?.emailAddress}
              </p>
            </div>
          )}
        </div>

        {!collapsed && (
          <ChevronDownIcon
            className={`w-4 h-4 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
            style={{ color: 'var(--color-text-muted)' }}
          />
        )}
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div
          className={`absolute bg-[var(--color-surface)] border border-[var(--color-border)] rounded-xl shadow-xl z-50 min-w-[240px] ${
            // The nested OrgSwitcher opens its own absolutely-positioned dropdown;
            // this panel is its containing block, so overflow-hidden (used for the
            // rounded corners otherwise) would clip it. Items are inset by the p-1
            // padding, so they clear the rounded corners without the clip anyway.
            showOrgSwitcher ? 'overflow-visible' : 'overflow-hidden'
          } ${dropdownPositionClass}`}
          style={{
            animation: 'scaleIn 150ms cubic-bezier(0.16, 1, 0.3, 1)'
          }}
        >
          <div className="p-1 space-y-1">
            {/* Org switcher — the same one the desktop sidebar uses, surfaced
                here for the mobile shell (showOrgSwitcher), whose top bar is the
                only chrome and otherwise has no way to switch orgs. Desktop
                leaves it off since the sidebar already renders OrgSwitcher up top. */}
            {showOrgSwitcher && (
              <>
                <OrgSwitcher collapsed={false} />
                <div className="h-px mx-2" style={{ background: 'var(--color-border)' }} />
              </>
            )}

            {/* Theme Selector Section */}
            <div className="px-3 py-2">
              <p className="text-xs font-medium mb-2" style={{ color: 'var(--color-text-muted)' }}>{t('theme')}</p>
              <ThemeSwitcher variant="grid" />
            </div>

            {/* Language Selector Section */}
            <div className="px-3 py-2 flex items-center justify-between gap-3">
              <p className="text-xs font-medium" style={{ color: 'var(--color-text-muted)' }}>{t('language')}</p>
              <LanguageSwitcher className="w-36" />
            </div>

            <div className="h-px mx-2" style={{ background: 'var(--color-border)' }} />

            {/* Navigation Links */}
            <Link
              href="/profile"
              className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors hover:bg-[var(--color-surface-hover)]"
              style={{ color: 'var(--color-text-primary)' }}
              onClick={() => setIsOpen(false)}
            >
              <UserCircleIcon className="w-5 h-5" style={{ color: 'var(--color-text-secondary)' }} />
              {t('profile')}
            </Link>

            {!hasPaymentOverride && (
              <button
                type="button"
                onClick={handleOpenBillingPortal}
                disabled={isOpeningBilling}
                className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors hover:bg-[var(--color-surface-hover)] disabled:opacity-60"
                style={{ color: 'var(--color-text-primary)' }}
              >
                <CreditCardIcon className="w-5 h-5" style={{ color: 'var(--color-text-secondary)' }} />
                {isOpeningBilling ? t('billingOpening') : t('billing')}
              </button>
            )}

            {canAccessSettings &&
              <Link
                href="/settings"
                className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors hover:bg-[var(--color-surface-hover)]"
                style={{ color: 'var(--color-text-primary)' }}
                onClick={() => setIsOpen(false)}
              >
                <Cog6ToothIcon className="w-5 h-5" style={{ color: 'var(--color-text-secondary)' }} />
                {t('settings')}
              </Link>
            }

            {/* When the org switcher is shown (mobile), it already renders its
                own operator-gated "Create organization" action, so suppress this
                one to avoid a duplicate row. */}
            {isOperator && !showOrgSwitcher && (
              <button
                onClick={() => {
                  setIsOpen(false);
                  setShowCreateOrgModal(true);
                }}
                className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors hover:bg-[var(--color-surface-hover)]"
                style={{ color: 'var(--color-text-primary)' }}
              >
                <PlusIcon className="w-5 h-5" style={{ color: 'var(--color-text-secondary)' }} />
                {t('createOrganization')}
              </button>
            )}

            <button
              type="button"
              onClick={() => {
                setIsOpen(false);
                openSupport();
              }}
              className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors hover:bg-[var(--color-surface-hover)]"
              style={{ color: 'var(--color-text-primary)' }}
            >
              <QuestionMarkCircleIcon className="w-5 h-5" style={{ color: 'var(--color-text-secondary)' }} />
              {t('support')}
            </button>

            {isAdmin && !hideDesktopOnlyLinks && (
              <a
                href={adminHref}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setIsOpen(false)}
                className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors hover:bg-[var(--color-surface-hover)]"
                style={{ color: 'var(--color-text-primary)' }}
              >
                <ShieldCheckIcon className="w-5 h-5" style={{ color: 'var(--color-text-secondary)' }} />
                {t('admin')}
              </a>
            )}

            {!hideDesktopOnlyLinks && <PartnerPortalLink onClose={() => setIsOpen(false)} />}

            <div className="h-px mx-2" style={{ background: 'var(--color-border)' }} />

            {/* Sign Out */}
            <button
              onClick={handleSignOut}
              className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors hover:bg-[var(--color-surface-hover)] text-red-500 hover:text-red-600"
            >
              <ArrowLeftOnRectangleIcon className="w-5 h-5" />
              {t('signOut')}
            </button>
          </div>
        </div>
      )}

      <CreateOrganizationModal
        isOpen={showCreateOrgModal}
        onClose={() => setShowCreateOrgModal(false)}
      />

      <style jsx>{`
        @keyframes scaleIn {
          from {
            opacity: 0;
            transform: scale(0.95) translateY(4px);
          }
          to {
            opacity: 1;
            transform: scale(1) translateY(0);
          }
        }
      `}</style>
    </div>
  );
}
